import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, Users, User } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { z } from "zod";

// Validation schema for reservation form
const reservationSchema = z.object({
  name: z.string()
    .trim()
    .min(1, "Name is required")
    .max(100, "Name must be less than 100 characters")
    .regex(/^[a-zA-Z\s\-'\.]+$/, "Name contains invalid characters"),
  email: z.string()
    .trim()
    .email("Please enter a valid email address")
    .max(255, "Email must be less than 255 characters"),
  phone: z.string()
    .trim()
    .min(1, "Phone number is required")
    .max(20, "Phone number is too long")
    .regex(/^[\d\s\+\-\(\)]+$/, "Please enter a valid phone number"),
  date: z.string()
    .min(1, "Date is required")
    .refine((val) => {
      const selectedDate = new Date(val);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return selectedDate >= today;
    }, "Date must be today or in the future")
    .refine((val) => {
      const selectedDate = new Date(val);
      const maxDate = new Date();
      maxDate.setDate(maxDate.getDate() + 90);
      return selectedDate <= maxDate;
    }, "Reservations can only be made up to 90 days in advance"),
  time: z.string()
    .min(1, "Time is required")
    .regex(/^(17|18|19|20|21|22|23):00$/, "Please select a valid time slot"),
  guests: z.string()
    .refine((val) => {
      if (val === "10+") return true;
      const num = parseInt(val);
      return !isNaN(num) && num >= 1 && num <= 10;
    }, "Please select a valid number of guests"),
  message: z.string()
    .max(500, "Special requests must be less than 500 characters")
    .optional()
    .transform(val => val?.trim() || ""),
});

export function ReservationSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    date: "",
    time: "",
    guests: "2",
    message: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormErrors({});
    setIsSubmitting(true);
    
    try {
      // Validate form data
      const validationResult = reservationSchema.safeParse(formData);
      
      if (!validationResult.success) {
        const errors: Record<string, string> = {};
        validationResult.error.errors.forEach((err) => {
          if (err.path[0]) {
            errors[err.path[0].toString()] = err.message;
          }
        });
        setFormErrors(errors);
        setIsSubmitting(false);
        toast({
          title: "Validation Error",
          description: "Please check the form for errors.",
          variant: "destructive"
        });
        return;
      }

      const validatedData = validationResult.data;
      
      // Parse guests - handle "10+" case
      const guestsValue = validatedData.guests === "10+" ? 10 : parseInt(validatedData.guests);

      const reservationData = {
        name: validatedData.name,
        email: validatedData.email,
        phone: validatedData.phone,
        date: validatedData.date,
        time: validatedData.time,
        guests: guestsValue,
        special_requests: validatedData.message || null,
        status: 'pending'
      };

      const { data, error } = await supabase.from('reservations').insert(reservationData).select().single();

      if (error) throw error;

      // Send email notification to admin (non-blocking)
      supabase.functions.invoke('send-reservation-email', {
        body: {
          type: 'new_reservation',
          reservation: {
            id: data.id,
            name: data.name,
            email: data.email,
            phone: data.phone,
            date: data.date,
            time: data.time,
            guests: data.guests,
            special_requests: data.special_requests
          }
        }
      }).catch(() => {
        // Silent failure for email - reservation is still saved
      });

      toast({
        title: "Reservation Request Sent!",
        description: "We'll confirm your booking shortly via email or phone.",
      });
      
      setFormData({
        name: "",
        email: "",
        phone: "",
        date: "",
        time: "",
        guests: "2",
        message: "",
      });
      setFormErrors({});
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit reservation. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  return (
    <section id="reservation" className="py-24 md:py-32 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-card/50 to-background" />

      <div className="container mx-auto px-4 relative" ref={ref}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-primary font-medium tracking-[0.2em] text-sm mb-4">
            BOOK YOUR TABLE
          </p>
          <h2 className="section-heading text-foreground underline-gold">
            RESERVATION
          </h2>
        </motion.div>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-2xl mx-auto"
        >
          <form onSubmit={handleSubmit} className="glass p-8 md:p-10 rounded-xl">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Name */}
              <div className="relative">
                <label className="block text-sm font-medium text-foreground/70 mb-2">
                  Name
                </label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    maxLength={100}
                    className={`w-full bg-secondary/50 border rounded-lg pl-12 pr-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 transition-all ${
                      formErrors.name 
                        ? 'border-destructive focus:border-destructive focus:ring-destructive' 
                        : 'border-border/50 focus:border-primary/50 focus:ring-primary/50'
                    }`}
                    placeholder="Your name"
                  />
                </div>
                {formErrors.name && (
                  <p className="text-destructive text-xs mt-1">{formErrors.name}</p>
                )}
              </div>

              {/* Email */}
              <div className="relative">
                <label className="block text-sm font-medium text-foreground/70 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  maxLength={255}
                  className={`w-full bg-secondary/50 border rounded-lg px-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 transition-all ${
                    formErrors.email 
                      ? 'border-destructive focus:border-destructive focus:ring-destructive' 
                      : 'border-border/50 focus:border-primary/50 focus:ring-primary/50'
                  }`}
                  placeholder="your@email.com"
                />
                {formErrors.email && (
                  <p className="text-destructive text-xs mt-1">{formErrors.email}</p>
                )}
              </div>

              {/* Phone */}
              <div className="relative">
                <label className="block text-sm font-medium text-foreground/70 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  maxLength={20}
                  className={`w-full bg-secondary/50 border rounded-lg px-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 transition-all ${
                    formErrors.phone 
                      ? 'border-destructive focus:border-destructive focus:ring-destructive' 
                      : 'border-border/50 focus:border-primary/50 focus:ring-primary/50'
                  }`}
                  placeholder="+66 XX XXX XXXX"
                />
                {formErrors.phone && (
                  <p className="text-destructive text-xs mt-1">{formErrors.phone}</p>
                )}
              </div>

              {/* Date */}
              <div className="relative">
                <label htmlFor="reservation-date" className="block text-sm font-medium text-foreground/70 mb-2">
                  Date
                </label>
                <div className="relative">
                  <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none" aria-hidden="true" />
                  <input
                    type="date"
                    id="reservation-date"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    required
                    min={new Date().toISOString().split('T')[0]}
                    max={new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                    aria-label="Select reservation date"
                    className={`w-full bg-secondary/50 border rounded-lg pl-12 pr-4 py-3 text-foreground focus:outline-none focus:ring-1 transition-all ${
                      formErrors.date 
                        ? 'border-destructive focus:border-destructive focus:ring-destructive' 
                        : 'border-border/50 focus:border-primary/50 focus:ring-primary/50'
                    }`}
                  />
                </div>
                {formErrors.date && (
                  <p className="text-destructive text-xs mt-1">{formErrors.date}</p>
                )}
              </div>

              {/* Time */}
              <div className="relative">
                <label htmlFor="reservation-time" className="block text-sm font-medium text-foreground/70 mb-2">
                  Time
                </label>
                <div className="relative">
                  <Clock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none" aria-hidden="true" />
                  <select
                    id="reservation-time"
                    name="time"
                    value={formData.time}
                    onChange={handleChange}
                    required
                    aria-label="Select reservation time"
                    className={`w-full bg-secondary/50 border rounded-lg pl-12 pr-4 py-3 text-foreground focus:outline-none focus:ring-1 transition-all appearance-none ${
                      formErrors.time 
                        ? 'border-destructive focus:border-destructive focus:ring-destructive' 
                        : 'border-border/50 focus:border-primary/50 focus:ring-primary/50'
                    }`}
                  >
                    <option value="">Select time</option>
                    <option value="17:00">5:00 PM</option>
                    <option value="18:00">6:00 PM</option>
                    <option value="19:00">7:00 PM</option>
                    <option value="20:00">8:00 PM</option>
                    <option value="21:00">9:00 PM</option>
                    <option value="22:00">10:00 PM</option>
                    <option value="23:00">11:00 PM</option>
                  </select>
                </div>
                {formErrors.time && (
                  <p className="text-destructive text-xs mt-1">{formErrors.time}</p>
                )}
              </div>

              {/* Guests */}
              <div className="relative md:col-span-2">
                <label htmlFor="reservation-guests" className="block text-sm font-medium text-foreground/70 mb-2">
                  Number of Guests
                </label>
                <div className="relative">
                  <Users className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none" aria-hidden="true" />
                  <select
                    id="reservation-guests"
                    name="guests"
                    value={formData.guests}
                    onChange={handleChange}
                    required
                    aria-label="Select number of guests"
                    className={`w-full bg-secondary/50 border rounded-lg pl-12 pr-4 py-3 text-foreground focus:outline-none focus:ring-1 transition-all appearance-none ${
                      formErrors.guests 
                        ? 'border-destructive focus:border-destructive focus:ring-destructive' 
                        : 'border-border/50 focus:border-primary/50 focus:ring-primary/50'
                    }`}
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                      <option key={num} value={num}>
                        {num} {num === 1 ? "Guest" : "Guests"}
                      </option>
                    ))}
                    <option value="10+">10+ Guests (Group)</option>
                  </select>
                </div>
                {formErrors.guests && (
                  <p className="text-destructive text-xs mt-1">{formErrors.guests}</p>
                )}
              </div>

              {/* Message */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-foreground/70 mb-2">
                  Special Requests (Optional)
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={3}
                  maxLength={500}
                  className={`w-full bg-secondary/50 border rounded-lg px-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 transition-all resize-none ${
                    formErrors.message 
                      ? 'border-destructive focus:border-destructive focus:ring-destructive' 
                      : 'border-border/50 focus:border-primary/50 focus:ring-primary/50'
                  }`}
                  placeholder="Birthday celebration, VIP seating, dietary requirements..."
                />
                {formErrors.message && (
                  <p className="text-destructive text-xs mt-1">{formErrors.message}</p>
                )}
              </div>
            </div>

            {/* Submit */}
            <Button type="submit" variant="hero" size="xl" className="w-full mt-8" disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : "Request Reservation"}
            </Button>

            <p className="text-center text-sm text-muted-foreground mt-4">
              We'll confirm your booking within 2 hours
            </p>
          </form>
        </motion.div>
      </div>
    </section>
  );
}
